/**
 * AdminLayout — the delivered navy-gold admin shell (ported from
 * `_design/ui_kits/admin/app.jsx`): a navy crest sidebar with grouped nav + gold
 * active rail + footer identity chip, and a gold-eyebrow header. Wraps the Refine
 * route outlet, preserving the live data layer underneath.
 */
import React from 'react';
import { Link, useLocation } from 'react-router';
import { useGetIdentity, useLogout } from '@refinedev/core';
import { LogOut, Search, Download, Bell } from './icons';
import { brand, initials } from '../theme/brand';
import { ADMIN_NAV, activeNav } from './adminNav';
import { AdminBreadcrumb } from './AdminBreadcrumb';
import { SectionTabs } from './SectionTabs';
import { EnvBadge, EnvSwitch, DemoBanner } from './EnvSwitch';
import { getActiveTenant } from '../lib/tenants';

interface Identity {
  name?: string;
  email?: string;
}

function Sidebar() {
  const { pathname } = useLocation();
  const { data: identity } = useGetIdentity<Identity>();
  const { mutate: logout } = useLogout();
  const name = identity?.name ?? 'Administrator';
  // Crest + wordmark for the resolved tenant (its subdomain). The slug is the
  // compact handle for the badge (muaa → "MUAA ADMIN"); the full label titles
  // the crest — spec §11 (crest + label theming).
  const tenant = getActiveTenant();
  const current = activeNav(pathname)?.item;

  return (
    <div
      style={{
        width: 248,
        flexShrink: 0,
        background: brand.navyGrad,
        color: brand.paper,
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        borderRight: '1px solid rgba(196,151,49,.25)',
      }}
    >
      <div
        style={{
          padding: '20px 20px 18px',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          borderBottom: '1px solid rgba(255,255,255,.08)',
        }}
      >
        <img
          src={tenant.crest ?? '/crest.png'}
          alt={tenant.label}
          style={{ width: 40, height: 40, objectFit: 'contain' }}
        />
        <div>
          <div style={{ fontFamily: brand.fDisplay, fontWeight: 800, fontSize: 15, letterSpacing: '.08em' }}>
            {`${tenant.slug.toUpperCase()} ADMIN`}
          </div>
          <div style={{ fontFamily: brand.fScript, fontSize: 17, lineHeight: 0.7, color: brand.gold3, marginTop: 2 }}>
            Alumni
          </div>
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '10px 10px 20px' }}>
        {ADMIN_NAV.map((g) => (
          <div key={g.group} style={{ marginBottom: 8 }}>
            <div
              style={{
                fontFamily: brand.fSans,
                fontWeight: 700,
                fontSize: 9.5,
                letterSpacing: '.16em',
                color: 'rgba(199,207,219,.6)',
                textTransform: 'uppercase',
                padding: '12px 12px 6px',
              }}
            >
              {g.group}
            </div>
            {g.items.map((it) => {
              const on = it === current;
              const Icon = it.icon;
              return (
                <Link
                  key={it.to}
                  to={it.to}
                  aria-current={on ? 'page' : undefined}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 11,
                    padding: '9px 12px',
                    borderRadius: 9,
                    marginBottom: 1,
                    textDecoration: 'none',
                    background: on ? 'rgba(196,151,49,.16)' : 'transparent',
                    color: on ? brand.gold3 : '#c7cfdb',
                    fontFamily: brand.fSans,
                    fontWeight: on ? 700 : 500,
                    fontSize: 13.5,
                    position: 'relative',
                  }}
                >
                  {on ? (
                    <span
                      style={{ position: 'absolute', left: 0, top: 8, bottom: 8, width: 3, borderRadius: 3, background: brand.goldGrad }}
                    />
                  ) : null}
                  <Icon size={17} color={on ? brand.gold3 : '#9aa6ba'} strokeWidth={on ? 2.2 : 1.8} />
                  {it.label}
                </Link>
              );
            })}
          </div>
        ))}
      </div>

      <div
        style={{
          padding: '12px 16px',
          borderTop: '1px solid rgba(255,255,255,.08)',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
        }}
      >
        <div
          style={{
            width: 34,
            height: 34,
            borderRadius: '50%',
            background: brand.jaguar,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: brand.fDisplay,
            fontWeight: 700,
            fontSize: 13,
            color: brand.paper,
            border: '1.5px solid ' + brand.gold,
            flexShrink: 0,
          }}
        >
          {initials(name)}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontFamily: brand.fSans,
              fontWeight: 600,
              fontSize: 12.5,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {name}
          </div>
          <div style={{ fontFamily: brand.fSans, fontWeight: 500, fontSize: 10.5, color: '#9aa6ba' }}>Administrator</div>
        </div>
        <button
          onClick={() => logout()}
          title="Sign out"
          aria-label="Sign out"
          style={{ background: 'transparent', border: 0, cursor: 'pointer', color: '#9aa6ba', display: 'flex' }}
        >
          <LogOut size={16} />
        </button>
      </div>
    </div>
  );
}

function Header() {
  const { pathname } = useLocation();
  const tenant = getActiveTenant();
  const active = activeNav(pathname);
  // Pages outside the curated sidebar (sessions, funds, activity, etc.) aren't in
  // ADMIN_NAV — derive a readable title from the path so the header reads e.g.
  // "Activity Feed" / "Event Sessions" instead of a misleading "Dashboard".
  const seg = pathname.split('/').filter(Boolean)[0] ?? '';
  const derived = seg.replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  const title = active?.tab?.label ?? active?.item.label ?? (pathname === '/' ? 'Dashboard' : derived || 'Dashboard');

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        padding: '16px 28px',
        borderBottom: '1px solid ' + brand.line,
        background: '#fff',
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        {/* Eyebrow slot: the brand line at the root, a breadcrumb trail on
            every sub-page (the CRUD-level default breadcrumb is disabled
            globally in App.tsx in favor of this one consistent placement). */}
        {pathname === '/' ? (
          <div
            style={{
              fontFamily: brand.fSans,
              fontWeight: 600,
              fontSize: 11,
              letterSpacing: '.12em',
              color: brand.gold,
              textTransform: 'uppercase',
            }}
          >
            {tenant.label}
          </div>
        ) : (
          <AdminBreadcrumb />
        )}
        <div style={{ fontFamily: brand.fDisplay, fontWeight: 800, fontSize: 22, color: brand.ink, marginTop: 1 }}>
          {title}
        </div>
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 9,
          background: brand.paper,
          border: '1px solid ' + brand.line,
          borderRadius: 10,
          padding: '9px 13px',
          width: 220,
          color: brand.sub,
        }}
      >
        <Search size={16} />
        <span style={{ fontFamily: brand.fSans, fontWeight: 500, fontSize: 13 }}>Search…</span>
      </div>
      <button
        style={{
          fontFamily: brand.fSans,
          fontWeight: 700,
          fontSize: 12.5,
          borderRadius: 10,
          padding: '10px 16px',
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          background: '#fff',
          color: brand.ink,
          border: '1px solid ' + brand.line,
        }}
      >
        <Download size={15} color={brand.gold} /> Export
      </button>
      {/* Glanceable DEV/LIVE pill: which backend (local vs hosted) THIS build is
          wired to, so the two look-alike dashboard tabs can't be confused. */}
      <EnvBadge />
      {/* Demo-mode: flip the dashboard's backend between prod and demo. Hidden
          unless the demo backend is configured (VITE_DEMO_SUPABASE_*). */}
      <EnvSwitch />
      <div
        style={{
          width: 38,
          height: 38,
          borderRadius: '50%',
          background: '#fff',
          border: '1px solid ' + brand.line,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
        }}
      >
        <Bell size={18} color={brand.ink} />
        <span
          style={{
            position: 'absolute',
            top: 8,
            right: 9,
            width: 7,
            height: 7,
            borderRadius: '50%',
            background: brand.gold4,
            border: '1.5px solid #fff',
          }}
        />
      </div>
    </div>
  );
}

export function AdminLayout({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', height: '100vh', background: brand.paper, fontFamily: brand.fSans }}>
      <Sidebar />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <Header />
        <SectionTabs />
        {/* Persistent "DEMO DATA" bar — shown on every page while on the demo backend. */}
        <DemoBanner />
        <div style={{ flex: 1, overflowY: 'auto' }}>{children}</div>
      </div>
    </div>
  );
}
